import React, { useState, useEffect, useRef } from 'react';
import Icon from '../../../components/AppIcon';

const PrivacySection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [cardsVisible, setCardsVisible] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
          [0, 1, 2].forEach((i) => setTimeout(() => setCardsVisible(prev => [...prev, i]), i * 150));
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const privacyFeatures = [
    {
      icon: 'Lock',
      title: 'Zero-Knowledge Encryption',
      description: 'Your journal entries are encrypted before they leave your device. Not even we can read them.',
      color: 'from-violet-500/20 to-purple-500/20'
    },
    {
      icon: 'Shield',
      title: 'AI Processing Without Storing',
      description: 'AI guidance is generated in real-time without permanently storing your personal reflections.',
      color: 'from-blue-500/20 to-cyan-500/20'
    },
    {
      icon: 'User',
      title: 'Full Control Over Your Data',
      description: 'Export or delete your data anytime. Your spiritual journey belongs to you alone.',
      color: 'from-emerald-500/20 to-teal-500/20'
    }
  ];

  return (
    <section id="privacy" ref={sectionRef} className="py-20 px-6 bg-background/50">
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h2 className="text-3xl md:text-5xl font-bold text-foreground mb-6">
            Your privacy is{' '}
            <span className="text-gradient">sacred.</span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Your reflections are deeply personal. We built the architecture to protect them — not just with a policy, but with how the system works.
          </p>
        </div>

        {/* Privacy Features */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {privacyFeatures.map((feature, index) => (
            <div
              key={index}
              className={`glassmorphism p-8 rounded-3xl text-center transition-all duration-700 ${
                cardsVisible.includes(index) ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              }`}
              style={{ transitionDelay: `${index * 130}ms` }}
            >
              <div className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${feature.color} flex items-center justify-center`}>
                <Icon name={feature.icon} size={28} className="text-primary" />
              </div>
              <h3 className="text-lg font-bold text-foreground mb-3">{feature.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{feature.description}</p>
            </div>
          ))}
        </div>

        {/* Trust Statement */}
        <div className={`text-center transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="glassmorphism p-8 rounded-3xl max-w-4xl mx-auto border border-primary/20">
            <Icon name="Heart" size={40} className="text-primary mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Your journey belongs to you alone.
            </h3>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We've built the entire architecture around one principle: no one else can access, read, or analyze your individual entries. Ever.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PrivacySection;
